import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Flex,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd'
import { Column } from '@ant-design/plots'
import { useStore } from 'effector-react'
import { useSearchParams } from 'react-router-dom'
import { createInfiniteList, fx, useInfiniteScroll } from '../../../libs'
import { $users, usersListMounted } from '../../usersRoles/users/list/model'
import { User } from '../../usersRoles/users/list/types'

type ReportRow = {
  id: string
  user_id: string
  username: string
  hour: string
  all_count: number
  approved_count: number
  rejected_count: number
  approved_less_than_15min_count: number
  approved_more_than_15min_count: number
  rejected_less_than_15min_count: number
  rejected_more_than_15min_count: number
}
type QuickPeriod = 'day' | 'week' | 'month'

const getReport = fx<void, { items: ReportRow[]; total: number }>({
  method: 'GET',
  url: '/verification/work-report',
})
const reportList = createInfiniteList(getReport)
const $report = reportList.$items

const localDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

const getQuickPeriodDates = (period: QuickPeriod): [string, string] => {
  const end = new Date()
  const start = new Date(end)

  if (period === 'week') {
    const daysSinceMonday = (end.getDay() + 6) % 7
    start.setDate(end.getDate() - daysSinceMonday)
  } else if (period === 'month') {
    start.setDate(1)
  }

  return [localDate(start), localDate(end)]
}

export const VerificationWorkReportPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const users = useStore($users)
  const rows = useStore($report)
  const loading = useStore(getReport.pending)
  const loadedPage = useStore(reportList.$loadedPage)
  const hasMore = useStore(reportList.$hasMore)
  const [dates, setDates] = useState<[string, string]>([
    searchParams.get('dateFrom') ??
      (searchParams.get('period') === 'week'
        ? getQuickPeriodDates('week')[0]
        : searchParams.get('period') === 'month'
          ? getQuickPeriodDates('month')[0]
          : localDate()),
    searchParams.get('dateTo') ?? localDate(),
  ])
  const [selectedUsers, setSelectedUsers] = useState<string[]>(
    searchParams.get('userIds')?.split(',').filter(Boolean) ?? [],
  )
  const [orderBy, setOrderBy] = useState(searchParams.get('orderBy') ?? 'hour')
  const [orderDirection, setOrderDirection] = useState<'ascend' | 'descend'>(
    searchParams.get('orderDirection') === 'descend' ? 'descend' : 'ascend',
  )
  const [chartZoom, setChartZoom] = useState(
    Number(searchParams.get('chartZoom')) || 1,
  )
  const [quickPeriod, setQuickPeriod] = useState<QuickPeriod | null>(() => {
    const period = searchParams.get('period')
    return period === 'day' || period === 'week' || period === 'month'
      ? period
      : null
  })
  const [requestedPage, setRequestedPage] = useState(1)
  const buildRequest = useCallback(
    (page: number) => {
      const query: Record<string, string> = {
        dateFrom: dates[0],
        dateTo: dates[1],
        orderBy,
        orderDirection,
        limit: '50',
        page: String(page),
      }
      if (selectedUsers.length) query.userIds = selectedUsers.join(',')
      return { query }
    },
    [dates, selectedUsers, orderBy, orderDirection],
  )
  const requestPage = useCallback(
    (page: number) => {
      setRequestedPage(page)
      getReport(buildRequest(page))
    },
    [buildRequest],
  )
  useEffect(() => {
    usersListMounted({
      limit: 500,
      page: 1,
      orderBy: 'username',
      orderDirection: 'ascend',
      searchText: { deletedAt: null },
    })
  }, [])
  useEffect(() => {
    requestPage(1)
  }, [requestPage])
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    next.set('dateFrom', dates[0])
    next.set('dateTo', dates[1])
    next.set('orderBy', orderBy)
    next.set('orderDirection', orderDirection)
    next.set('chartZoom', String(chartZoom))
    if (quickPeriod) next.set('period', quickPeriod)
    else next.delete('period')
    if (selectedUsers.length) next.set('userIds', selectedUsers.join(','))
    else next.delete('userIds')
    setSearchParams(next, { replace: true })
  }, [dates, selectedUsers, orderBy, orderDirection, chartZoom, quickPeriod])
  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: loading,
    onLoadMore: () => requestPage(loadedPage + 1),
  })
  const isLongPeriod =
    (new Date(`${dates[1]}T00:00:00Z`).getTime() -
      new Date(`${dates[0]}T00:00:00Z`).getTime()) /
      86_400_000 +
    1 >
    3
  // On a long range, increased zoom exposes the original hourly data.
  const chartByDay = isLongPeriod && chartZoom < 1.5
  const chartData = useMemo(
    () => {
      const grouped = new Map<
        string,
        { period: string; employee: string; count: number }
      >()
      rows.forEach((row) => {
        const date = new Date(row.hour)
        const period = chartByDay
          ? date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
          : date.toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
            })
        const key = `${period}-${row.username}`
        const current = grouped.get(key)
        grouped.set(key, {
          period,
          employee: row.username,
          count: (current?.count ?? 0) + Number(row.all_count),
        })
      })
      return Array.from(grouped.values())
    },
    [rows, chartByDay],
  )
  const chartPeriods = new Set(chartData.map((item) => item.period)).size
  const chartWidth = Math.max(900, chartPeriods * 90 * chartZoom)
  const employeeOptions = useMemo(
    () =>
      (users as User[])
        .filter((user) => user.deletedAt === null)
        .map((user) => {
          const fullName = [user.firstName, user.lastName]
            .filter(Boolean)
            .join(' ')
          const label = fullName && fullName !== user.username
            ? `${fullName} (${user.username})`
            : user.username

          return { label, value: user.id }
        }),
    [users],
  )
  const employeeLabels = useMemo(
    () => new Map(employeeOptions.map((option) => [option.value, option.label])),
    [employeeOptions],
  )
  const columns = [
    {
      title: 'Employee',
      dataIndex: 'username',
      sorter: true,
      sortOrder: orderBy === 'username' ? orderDirection : undefined,
    },
    {
      title: 'Hour',
      dataIndex: 'hour',
      sorter: true,
      sortOrder: orderBy === 'hour' ? orderDirection : undefined,
      render: (v: string) => new Date(v).toLocaleString('ru-RU'),
    },
    { title: 'All', dataIndex: 'all_count' },
    { title: 'Approved', dataIndex: 'approved_count' },
    { title: 'Rejected', dataIndex: 'rejected_count' },
    { title: 'Approved <=15m', dataIndex: 'approved_less_than_15min_count' },
    { title: 'Approved >15m', dataIndex: 'approved_more_than_15min_count' },
    { title: 'Rejected <=15m', dataIndex: 'rejected_less_than_15min_count' },
    { title: 'Rejected >15m', dataIndex: 'rejected_more_than_15min_count' },
  ]
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={1}>Hourly verification work</Typography.Title>
      <Flex gap="middle" wrap>
        <Flex gap="small" align="center">
          <Typography.Text type="secondary">Period:</Typography.Text>
          {(
            [
              ['day', 'Day'],
              ['week', 'Week'],
              ['month', 'Month'],
            ] as [QuickPeriod, string][]
          ).map(([period, label]) => (
            <Button
              key={period}
              type={quickPeriod === period ? 'primary' : 'default'}
              onClick={() => {
                setQuickPeriod(period)
                setDates(getQuickPeriodDates(period))
              }}
            >
              {label}
            </Button>
          ))}
        </Flex>
        <DatePicker.RangePicker
          allowClear={false}
          onChange={(_, values) => {
            if (values[0] && values[1]) {
              setQuickPeriod(null)
              setDates(values as [string, string])
            }
          }}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLocaleLowerCase()
              .includes(input.toLocaleLowerCase())
          }
          labelRender={({ value, label }) =>
            employeeLabels.get(String(value)) ?? label ?? 'Loading employee...'
          }
          placeholder="Employees"
          options={employeeOptions}
          value={selectedUsers}
          onChange={setSelectedUsers}
          style={{ minWidth: 320 }}
          maxTagCount="responsive"
        />
      </Flex>
      <Typography.Text type="secondary">
        Both selected dates are included: from 00:00 of the first day through
        23:59:59 of the last day.
      </Typography.Text>
      <Card
        title={
          chartByDay
            ? 'Total requests processed by day'
            : 'Total requests processed by hour'
        }
      >
        <Spin spinning={loading}>
          {chartData.length > 0 && (
            <>
              <Flex align="center" gap="small" style={{ marginBottom: 8 }}>
                <Typography.Text type="secondary">Zoom</Typography.Text>
                <Button
                  size="small"
                  onClick={() => setChartZoom((value) => Math.max(0.75, value - 0.25))}
                >
                  −
                </Button>
                <input
                  type="range"
                  min="0.75"
                  max="3"
                  step="0.25"
                  value={chartZoom}
                  onChange={(event) => setChartZoom(Number(event.target.value))}
                  aria-label="Chart zoom"
                />
                <Button
                  size="small"
                  onClick={() => setChartZoom((value) => Math.min(3, value + 0.25))}
                >
                  +
                </Button>
                <Typography.Text type="secondary">
                  {Math.round(chartZoom * 100)}%
                </Typography.Text>
              </Flex>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <Column
                  data={chartData}
                  xField="period"
                  yField="count"
                  colorField="employee"
                  isGroup
                  width={chartWidth}
                  height={300}
                  axis={{
                    x: {
                      label: {
                        autoRotate: true,
                        autoHide: false,
                        style: { fontSize: 10 },
                      },
                    },
                    y: { label: { style: { fontSize: 10 } } },
                  }}
                  legend={{ position: 'top' }}
                  tooltip={{
                    title: (datum: { period: string }) => datum.period,
                  }}
                />
              </div>
            </>
          )}
          <Table
            rowKey={(r) => `${r.user_id}-${r.hour}`}
            columns={columns}
            dataSource={rows}
            pagination={false}
            onChange={(_, __, sorter) => {
              const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
              if (currentSorter?.field && currentSorter.order) {
                setOrderBy(String(currentSorter.field))
                setOrderDirection(currentSorter.order)
              }
            }}
            scroll={{ x: 1200 }}
          />
          <div ref={sentinelRef} style={{ textAlign: 'center', padding: 16 }}>
            {loading && requestedPage > 1 && <Spin size="small" />}
          </div>
        </Spin>
      </Card>
    </Space>
  )
}
